/**
 * RSC Advanced — ROI Searcher ranking helper (PURE, no React / no engine state).
 *
 * Takes an array of *hydrated* candidates — each already carrying the priced +
 * saving-derived fields the RoiSearcherView computes BEFORE calling rankPlans:
 *
 *   {
 *     pkg,            // the candidate object the engine prices (kwp, panels, battQty…)
 *     monthlySaving,  // RM/month (engine calcSaving, capped at bill)
 *     yearlySaving,   // RM/year  (monthlySaving × 12)
 *     netCash,        // RM cash price after promos (engine.getPrice cash)
 *     roiYears,       // netCash / yearlySaving
 *     savingsPct,     // monthlySaving / bill × 100   (0..~100)
 *     ... (kwp, panels, battType, battQty, etc. for de-dupe + display)
 *   }
 *
 * Returns three picks:
 *   - fastest   = lowest roiYears (quickest payback)
 *   - highValue = savingsPct closest to 80  (the "sweet spot" balance)
 *   - max       = highest savingsPct, capped at 100 (best bill kill)
 *
 * De-dupe key = `${panels}-${battery}` so the same physical config (panel count +
 * battery total kWh) never appears twice in the pool before ranking. Returns nulls
 * gracefully when the pool is empty (or after de-dupe leaves nothing usable).
 */

const HIGH_VALUE_TARGET = 80; // savingsPct the "high-value" pick aims closest to

// Stable de-dupe key for a candidate. `pkg` may live at the top level OR nested
// under `.pkg` (the view passes the hydrated shape with both), so read from either.
function dedupeKey(c) {
    const src = c && c.pkg ? c.pkg : c;
    const panels = src && src.panels != null ? src.panels : (c && c.panels);
    const battery = src && src.battKwhTotal != null
        ? src.battKwhTotal
        : (c && c.battKwhTotal != null ? c.battKwhTotal : (src && src.battQty));
    return `${panels}-${battery}`;
}

export function rankPlans(candidates) {
    const empty = { fastest: null, highValue: null, max: null };
    if (!Array.isArray(candidates) || candidates.length === 0) return empty;

    // De-dupe by panels-battery. Keep the FIRST occurrence (callers pass a stable order).
    const seen = new Set();
    const pool = [];
    for (const c of candidates) {
        if (!c) continue;
        const k = dedupeKey(c);
        if (seen.has(k)) continue;
        seen.add(k);
        pool.push(c);
    }
    if (pool.length === 0) return empty;

    // Fastest ROI — lowest roiYears (treat missing/non-positive ROI as +Infinity so
    // a candidate with no payback never wins).
    const roiOf = (c) => {
        const r = Number(c.roiYears);
        return Number.isFinite(r) && r > 0 ? r : Infinity;
    };
    let fastest = null;
    for (const c of pool) {
        if (roiOf(c) === Infinity) continue;
        if (!fastest || roiOf(c) < roiOf(fastest)) fastest = c;
    }

    // High-value — savingsPct closest to the 80% target (abs distance).
    const distOf = (c) => Math.abs((Number(c.savingsPct) || 0) - HIGH_VALUE_TARGET);
    let highValue = null;
    for (const c of pool) {
        if (!highValue || distOf(c) < distOf(highValue)) highValue = c;
    }

    // Max savings — highest savingsPct that does not exceed 100. If every candidate
    // is over 100 (shouldn't happen — calcSaving caps at bill), fall back to the
    // highest overall so a pick is still returned.
    const capped = pool.filter(c => (Number(c.savingsPct) || 0) <= 100);
    const maxPool = capped.length > 0 ? capped : pool;
    let max = null;
    for (const c of maxPool) {
        if (!max || (Number(c.savingsPct) || 0) > (Number(max.savingsPct) || 0)) max = c;
    }

    return { fastest, highValue, max };
}

export default rankPlans;
